"use client";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import { forgotPassword } from "../services/Service";
import { mapRole } from "../utils/roleMapper"; // ✅ NEW

import { FaEye, FaEyeSlash } from "react-icons/fa";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CryptoJS from "crypto-js";

const readStoredValue = (key) => localStorage.getItem(key) || sessionStorage.getItem(key) || "";
const setStoredValue = (key, value) => {
  const normalized = value ?? "";
  localStorage.setItem(key, normalized);
  sessionStorage.setItem(key, normalized);
};

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [timer, setTimer] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showForgotPasswordForm, setShowForgotPasswordForm] = useState(false);
  const [resetEmail, setResetEmail] = useState("");

  const validatePassword = (password) => {
    const startsWithCapital = /^[A-Z]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const minLength = password.length >= 6;

    if (!startsWithCapital || !hasSpecialChar || !minLength) {
      setError("The password you entered for the username user is incorrect.");
      setFailedAttempts((prev) => prev + 1);
      setPassword("");
      return false;
    }
    return true;
  };

  useEffect(() => {
    let interval = null;
    if (isLocked && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setIsLocked(false);
            setFailedAttempts(0);
            setEmail("");
            setPassword("");
            setError(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isLocked, timer]);

  const handleLogin = async (e) => {
    e.preventDefault();
    toast.dismiss();
    setError(null);
    setLoading(true);

    const passphrase = process.env.REACT_APP_PASSPHRASE;
    const encryptedPassword = CryptoJS.AES.encrypt(password, passphrase).toString();

    try {
      const response = await login(email, encryptedPassword);

      if (response && response.status === "success") {
        const payload = response.data || {};

        // Block inactive users before any persistence or navigation happens.
        // Missing status falls through to the existing active-login behavior.
        if (payload.status === 0) {
          const inactiveMessage =
            "Your account is inactive. Please contact your administrator.";
          setError(inactiveMessage);
          toast.error(inactiveMessage);
          setFailedAttempts((prev) => prev + 1);
          setPassword("");
          if (failedAttempts + 1 >= 3 && timer === 0) {
            setIsLocked(true);
            setTimer(60);
          }
          return;
        }

        const username = payload.username || payload.email || "";
        const user_name = payload.user_name || payload.name || "";
        const user_id = payload.user_id || payload.id || "";
        const abbreviation = payload.abbreviation || payload.roleKey || "";
        const existingClientId = readStoredValue("clientId");
        const client_id = payload.client_id || payload.ClientId || existingClientId || "";

        // ✅ UPDATED: central role mapping
        const role = mapRole(abbreviation);
        if (abbreviation === "L") {
          navigate("/admin/components/Inquires")
          return
        }
        setStoredValue("clientId", client_id);
        setStoredValue("user_name", user_name);
        setStoredValue("role", role);
        setStoredValue("email", username);
        setStoredValue("userId", user_id);
        setStoredValue("abbreviation", abbreviation);

        setError(null);
        setFailedAttempts(0);

        // ✅ UPDATED: role-based navigation
        if (role === "admin") {
          navigate("/admin");
          return;
        }
        if (role === "ticketbot-admin") {
          navigate("/admin");
          return;
        }
        if (role === "leadbot-admin") {
          navigate("/admin");
          return;
        }
        if (role === "client-admin") {
          navigate("/client-admin");
          return;
        }
        if (role === "agent") {
          navigate("/agent");
          return;
        }
        if (role === "superadmin") {
          navigate("/superadmin");
          return;
        }

        setError(`Invalid role. Please contact support. (role: ${abbreviation})`);
        setFailedAttempts((prev) => prev + 1);
        setPassword("");
        if (failedAttempts + 1 >= 3 && timer === 0) {
          setIsLocked(true);
          setTimer(60);
        }

      } else {
        const msg = response?.message || "Invalid email or password.";
        setError(msg);
        toast.error(msg);
        setFailedAttempts((prev) => prev + 1);
        setPassword("");
        if (failedAttempts + 1 >= 3 && timer === 0) {
          setIsLocked(true);
          setTimer(60);
        }
      }
    } catch (err) {
      console.error("Login Error:", err);
      const message = err.message || "Login failed. Please try again.";
      setError(message);
      toast.error(message);
      setFailedAttempts((prev) => prev + 1);
      setPassword("");
      if (failedAttempts + 1 >= 3 && timer === 0) {
        setIsLocked(true);
        setTimer(60);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    try {
      await forgotPassword(resetEmail);
      toast.success("Password reset link sent to your email.");
      setResetEmail("");
      setShowForgotPasswordForm(false);
    } catch (error) {
      toast.error(error.message || "Failed to send reset email.");
    }
  };

  return (
    <div
      className="min-vh-100 d-flex align-items-center"
      style={{
        background:
          "radial-gradient(circle at top left, #222bff 0, #111827 35%, #020617 100%)",
      }}
    >
      <div className="container">
        <div className="row g-0 justify-content-center">
          <div className="col-12 col-xl-10">
            <div className="row g-0 shadow-lg rounded-4 overflow-hidden bg-white bg-opacity-5">

              {/* LEFT PANEL */}
              <div
                className="col-lg-6 d-none d-lg-flex flex-column justify-content-between p-4 p-xl-5 text-white"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(79,70,229,0.95), rgba(30,64,175,0.95))",
                }}
              >
                <div>
                  <span className="badge bg-light text-primary fw-semibold mb-3">
                    ATai Bot Dashboard
                  </span>

                  <h1 className="fw-bold mb-3" style={{ fontSize: "2.2rem", lineHeight: 1.15 }}>
                    Smarter Ticketing.
                    <br />
                    Faster Resolutions.
                  </h1>

                  <p className="mb-4 text-light">
                    It helps you centralise customer queries, automate responses, and give
                    your team a single view of every interaction — in real time.
                  </p>

                  <ul className="list-unstyled small mb-4">
                    <li className="mb-2 d-flex align-items-start">
                      <span className="me-2 mt-1 rounded-circle bg-light bg-opacity-10 p-1">✓</span>
                      Auto-assisted triage and ticket assignment
                    </li>
                    <li className="mb-2 d-flex align-items-start">
                      <span className="me-2 mt-1 rounded-circle bg-light bg-opacity-10 p-1">✓</span>
                      Unified view of leads, support tickets, and follow-ups
                    </li>
                    <li className="mb-2 d-flex align-items-start">
                      <span className="me-2 mt-1 rounded-circle bg-light bg-opacity-10 p-1">✓</span>
                      Designed for SMEs to scale without heavy CRM costs
                    </li>
                  </ul>
                </div>

                <div className="mt-3 small text-white-50">
                  Powered by<span className="fw-semibold text-white"> ATai Bot</span> © {new Date().getFullYear()} All rights reserved.
                </div>
              </div>

              {/* RIGHT PANEL */}
              <div className="col-12 col-lg-6 bg-white p-4 p-sm-5">

                <div className="d-flex justify-content-end mb-4">
                  <img
                    src="https://ataibot.in/atai-logo.png"
                    alt="ATai Bot"
                    style={{ height: "38px" }}
                  />
                </div>

                <div className="mb-4">
                  <h3 className="fw-semibold mb-1">Sign in</h3>
                  <p className="text-muted small mb-0">
                    Use your work email to access your dashboard.
                  </p>
                </div>

                <form onSubmit={handleLogin}>
                  <div className="mb-3">
                    <label htmlFor="email" className="form-label fw-medium">User ID</label>
                    <input
                      type="email"
                      className="form-control form-control-lg"
                      id="email"
                      placeholder="Enter Registered Email ID"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={isLocked || loading}
                    />
                  </div>

                  <div className="mb-3 position-relative">
                    <label htmlFor="password" className="form-label fw-medium">Password</label>
                    <div className="input-group">
                      <input
                        type={showPassword ? "text" : "password"}
                        className="form-control form-control-lg"
                        id="password"
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        disabled={isLocked || loading}
                      />
                      <button
                        type="button"
                        className="btn btn-link text-muted position-absolute top-50 end-0 translate-middle-y pe-3"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{ textDecoration: "none" }}
                        disabled={isLocked || loading}
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>

                    {isLocked && timer > 0 && (
                      <div className="text-muted small mt-1 text-end">
                        Too many attempts. Try again in {timer}s.
                      </div>
                    )}
                  </div>

                  {error && (
                    <div className="alert alert-danger py-2 small">{error}</div>
                  )}

                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <small className="text-muted">Need help? Contact Support</small>

                    {!showForgotPasswordForm && (
                      <button
                        type="button"
                        className="btn btn-link p-0 small"
                        onClick={() => setShowForgotPasswordForm(true)}
                      >
                        Forgot password?
                      </button>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary w-100 btn-lg"
                    disabled={isLocked || loading}
                    style={{ borderRadius: "999px" }}
                  >
                    {loading ? (
                      <span className="spinner-border spinner-border-sm"></span>
                    ) : (
                      "Login"
                    )}
                  </button>
                </form>

                {showForgotPasswordForm && (
                  <div className="mt-4 border-top pt-3">
                    <h6 className="mb-2">Reset your password</h6>
                    <p className="text-muted small mb-3">
                      Enter the email linked to your ATai Bot account and we'll send a reset link.
                    </p>

                    <form onSubmit={handleForgotPasswordSubmit}>
                      <div className="mb-2">
                        <input
                          type="email"
                          className="form-control"
                          placeholder="you@company.com"
                          value={resetEmail}
                          onChange={(e) => setResetEmail(e.target.value)}
                          required
                        />
                      </div>

                      <div className="d-flex gap-2">
                        <button type="submit" className="btn btn-secondary flex-grow-1">
                          Send reset link
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => setShowForgotPasswordForm(false)}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
